/**
 * A wave that moves with the audio. Story VC-016.
 *
 * # What it draws, and why not bars
 *
 * The time-domain signal, smoothed into a symmetric ribbon. A bar spectrum
 * is the conventional choice and it is the wrong one here: a caller looks at
 * this to answer "is it hearing me" and "is it still talking", and both are
 * questions about an *envelope*. A spectrum answers a question about timbre,
 * which nobody is asking, and it moves in a way that looks identical for
 * speech and for a fan in the room.
 *
 * So: the waveform's own magnitude, resampled to a fixed number of columns
 * and drawn as a filled curve mirrored about the centre. It reads as a voice.
 *
 * # Why a canvas and not SVG
 *
 * Sixty frames a second of forty-odd path segments. In SVG that is forty DOM
 * nodes rewritten per frame through React, which is a lot of reconciliation
 * for something with no interactive parts. The canvas draws from a `ref` and
 * never re-renders the component at all — the animation loop lives outside
 * React by design, for the same reason the audio does.
 *
 * # Silence is drawn, not hidden
 *
 * A flat line at rest rather than a component that disappears. A meter that
 * vanished when nothing was heard would look identical to a meter that had
 * broken, and "the microphone stopped working" is a support call.
 */

import { useEffect, useRef } from "react";

/** Columns in the ribbon. Enough to read as a wave, few enough to draw. */
const COLUMNS = 48;

/**
 * How fast a column falls back towards rest, per frame.
 *
 * Rise is immediate and fall is damped, which is a VU meter's asymmetry and
 * the reason one looks alive: a peak that decayed as fast as it arrived
 * flickers, and a peak that decayed slowly enough to be smooth would still
 * be lit after the speaker stopped.
 */
const FALL = 0.14;

/**
 * @param {{ analyser: AnalyserNode | null | undefined,
 *           label: string,
 *           tone?: "primary" | "tertiary",
 *           active?: boolean }} props
 */
export function Equalizer({ analyser, label, tone = "primary", active = true }) {
  const canvasRef = useRef(null);
  // The decayed magnitudes, kept across frames outside React: they are
  // animation state, and putting them in `useState` would re-render the tree
  // sixty times a second to change nothing above this element.
  const levelsRef = useRef(new Float32Array(COLUMNS));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }

    const samples = analyser ? new Uint8Array(analyser.fftSize) : null;
    let frame = 0;

    const draw = () => {
      frame = requestAnimationFrame(draw);

      // The element's own size in device pixels, read each frame: a phone
      // rotating and a window resizing both change it, and a canvas whose
      // backing store is stale draws a blurred wave.
      const ratio = Math.min(globalThis.devicePixelRatio ?? 1, 2);
      const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      const levels = levelsRef.current;
      if (analyser && samples && active) {
        analyser.getByteTimeDomainData(samples);
        // One column per slice, taking the slice's peak deviation from the
        // 128 midpoint. Peak rather than RMS: a consonant is short and loud,
        // and an average flattens exactly the part of speech a listener
        // uses to tell speech from noise.
        const per = Math.floor(samples.length / COLUMNS) || 1;
        for (let column = 0; column < COLUMNS; column += 1) {
          let peak = 0;
          for (let index = 0; index < per; index += 1) {
            const value = Math.abs(samples[column * per + index] - 128) / 128;
            if (value > peak) {
              peak = value;
            }
          }
          // A gentle curve, because voices spend most of their time quiet
          // and a linear meter therefore spends most of its time flat.
          const shaped = Math.min(1, peak ** 0.7 * 1.25);
          levels[column] =
            shaped > levels[column]
              ? shaped
              : levels[column] + (shaped - levels[column]) * FALL;
        }
      } else {
        for (let column = 0; column < COLUMNS; column += 1) {
          levels[column] *= 1 - FALL;
        }
      }

      paint(context, canvas, levels, tone);
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [analyser, active, tone]);

  return (
    <div className="meter">
      <span className="meter__label">{label}</span>
      <canvas
        ref={canvasRef}
        className="meter__canvas"
        // The wave carries no information a reader cannot get from the
        // transcript, so it is decorative and the label beside it is the
        // accessible name of the row.
        aria-hidden="true"
      />
    </div>
  );
}

/**
 * One frame.
 *
 * Colours are read from the computed style rather than hard-coded, so the
 * wave follows the M3 role it was given — including across a light/dark
 * switch, which happens without this component remounting.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {HTMLCanvasElement} canvas
 * @param {Float32Array} levels
 * @param {"primary" | "tertiary"} tone
 */
function paint(context, canvas, levels, tone) {
  const { width, height } = canvas;
  const style = getComputedStyle(canvas);
  const ink =
    style.getPropertyValue(`--md-sys-color-${tone}`).trim() || "currentColor";
  const rest =
    style.getPropertyValue("--md-sys-color-outline-variant").trim() || ink;

  context.clearRect(0, 0, width, height);

  const middle = height / 2;
  const step = width / (COLUMNS - 1);
  // A floor, so the ribbon is a line at rest rather than nothing at all.
  const floor = Math.max(1, height * 0.012);
  const reach = middle - floor;

  const top = [];
  const bottom = [];
  for (let column = 0; column < COLUMNS; column += 1) {
    const x = column * step;
    // The ends taper, so the ribbon reads as a shape rather than as a
    // rectangle that happens to wobble.
    const taper = Math.sin((column / (COLUMNS - 1)) * Math.PI) ** 0.5;
    const y = levels[column] * reach * taper + floor;
    top.push([x, middle - y]);
    bottom.push([x, middle + y]);
  }

  context.beginPath();
  trace(context, top);
  trace(context, bottom.reverse(), true);
  context.closePath();

  // Loud is the role's own colour, quiet fades towards the outline — so a
  // silent meter is visibly present and visibly idle.
  const loudest = levels.reduce((peak, value) => Math.max(peak, value), 0);
  context.fillStyle = loudest > 0.03 ? ink : rest;
  context.globalAlpha = 0.25 + 0.6 * Math.min(1, loudest * 1.6);
  context.fill();
  context.globalAlpha = 1;
}

/**
 * A smooth curve through the points, using the midpoints as the anchors.
 *
 * Quadratic segments rather than straight ones: the columns are a coarse
 * sampling of a continuous signal, and joining them with lines draws the
 * sampling rather than the sound.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {number[][]} points
 * @param {boolean} [continuing]
 */
function trace(context, points, continuing = false) {
  if (!points.length) {
    return;
  }
  if (continuing) {
    context.lineTo(points[0][0], points[0][1]);
  } else {
    context.moveTo(points[0][0], points[0][1]);
  }
  for (let index = 1; index < points.length - 1; index += 1) {
    const [x, y] = points[index];
    const [nextX, nextY] = points[index + 1];
    context.quadraticCurveTo(x, y, (x + nextX) / 2, (y + nextY) / 2);
  }
  const last = points[points.length - 1];
  context.lineTo(last[0], last[1]);
}
